<template>
  <div class="p-1">
    <v-navigation-drawer
      v-if="this.configuration != null"
      width="420px"
      fixed
      right
      :temporary="isWindowTemporary"
      :permanent="isWindowPermanent"
      v-model="drawer"
      class="h-100 d-grid-nav"
    >
      <v-card class="elevation-0">
        <v-card-title :style="backgroundStarStyle">
          <v-icon size="35" class="black--text">show_chart</v-icon>
          <span class="headline black--text">Plot Configuration</span>
          <v-spacer></v-spacer>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Parametric Studies Plot Configuration"
          >
            One of the main features offered by the this module is the ability
            to compute a mission with varying parameters. The plots can be used
            to see how those variations influence the results.
            <br />
            <br />
            <b>Example</b>: an orbital transfer mission is defined from an orbit
            at 600 km of altitude to an orbit at 700 km. At the end of the
            computation, a single result is generated and only one value of ΔV
            is displayed. The plot interface is useless in that case.
            <br />
            Now define the target orbit with a range of altitudes from 700 to
            800 km. The computation will generate several results and thus a
            range of values for ΔV. The plot interface can be used to display
            the ΔV required depending on the target orbit altitude.
            <br />
            <br />
            <h5>Plot types</h5>
            <b>Line/Scatter chart</b>: 2 dimensional chart,
            <i>parameters</i> can be used to display several lines (see
            <i>data choice</i> below).<br />
            <b>Multi axis</b>: several Y-axis to look at different results
            depending on the same parameter.<br />
            <b>Heat map / Contour map</b>: a graphical representation of data
            where the individual values contained in a matrix are represented as
            colors. Used to display a result depending on two varying
            parameters.<br />
            <b>Bar chart</b>: a vertical bar chart, <i>parameters</i> can be
            used for comparison (see <i>data choice</i> below).
            <br />
            <br />
            <h5>Data choice</h5>
            When several parameters vary, it can be hard to know what to plot
            and to find the correct configuration. Several settings are designed
            to simplify this task.
            <br />
            <b>Filters</b>: filters can be used to limit the results. Only the
            results matching the filters will be displayed.
            <br />
            In the example above, filtering on final altitudes of 690 km and 700
            km will provide only 2 results. Then the choice of data to plot
            remains the same.
            <br />
            <b>Display non evolving fields</b>: by default only the fields which
            are not constant are displayed. This limits the choice of fields but
            it might hide some needed data. This behaviour can be overloaded by
            checking the option <i>Display non evolving fields</i>.
            <br />
            In that case all the result fields will be displayed.<br />
            <b>Parameters</b>: this option is only available for line/scatter
            charts. A curve will be displayed for each combination of
            parameters.<br />
            In the example above, add a varying inclination on the final orbit,
            e.g. use the two values 60 and 70 degrees. Also configure the
            spacecraft to use 2 different propulsion systems.<br />
            Then the inclination and propulsion system name can be selected as
            parameters in the chart. 4 curves will be displayed :
            60deg/thruster1, 60deg/thruster2, 70deg/thruster1,
            70deg/thruster2<br />
            <br />
            <h5>Customization</h5>
            <b>Display mode</b>: results can be displayed as lines, markers
            (disconnected dots) with or without attached text.<br />
            <b>Lines shape</b>:
            <ul style="margin: 0">
              <li>linear: straight line between points</li>
              <li>spline: interpolation for smooth rendering</li>
            </ul>
            <b>Axes</b>: name and unit <br />
            <br />
          </exo-help-dialog>
        </v-card-title>
        <v-card-text class="p-0 pt-2">
          <v-form>
            <v-col class="px-3">
              <v-text-field
                horizontal
                label="Title"
                v-model="configuration.name"
                maxlength="100"
              >
              </v-text-field>
              <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="14"></div>
              <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="18"></div>
            </v-col>
            <v-col class="px-3">
              <exo-filters-config :root="_self"></exo-filters-config>
              <v-checkbox
                id="inputnotevolving"
                v-model="configuration.displayNonEvolvingFields"
                label="Display non-evolving fields"
              ></v-checkbox>
            </v-col>
            <v-divider class="mt-0"></v-divider>
            <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="20"></div>
            <v-col class="px-3">
              <v-select
                :items="plotTypes"
                label="Type"
                v-model="configuration.type"
                :menu-props="{ transition: 'scale-transition' }"
              ></v-select>
              <!--make functon-->
              <v-select
                v-if="isLineChart"
                :items="scatterModesLineChart"
                item-value="value"
                item-text="label"
                label="Display mode"
                v-model="configuration.scatterMode"
                :menu-props="{ transition: 'scale-transition' }"
              >
                <template slot="selection" slot-scope="{ item }">
                  {{ item.label }}
                </template>
                <template slot="item" slot-scope="{ item }">
                  {{ item.label }}
                </template>
              </v-select>
              <v-select
                v-if="isMultiAxis"
                :items="scatterModesMultiAxis"
                item-value="value"
                item-text="label"
                label="Display mode"
                v-model="configuration.scatterMode"
                :menu-props="{ transition: 'scale-transition' }"
              >
                <template slot="selection" slot-scope="{ item }">
                  {{ item.label }}
                </template>
                <template slot="item" slot-scope="{ item }">
                  {{ item.label }}
                </template>
              </v-select>
              <v-select
                v-if="displayScatterShape"
                :items="scatterShapes"
                item-value="value"
                item-text="label"
                label="Line shape"
                v-model="configuration.scatterShape"
                :menu-props="{ transition: 'scale-transition' }"
              >
                <template slot="selection" slot-scope="{ item }">
                  {{ item.label }}
                </template>
                <template slot="item" slot-scope="{ item }">
                  {{ item.label }}
                </template>
              </v-select>
              <exo-select-field
                v-if="displayScatterText"
                :field="configuration.scatterText"
                :root="_self"
              />
            </v-col>
            <v-divider class="mt-0"></v-divider>
            <v-col class="px-3">
              <exo-path-select-row
                v-if="isMap"
                :field="configuration.z"
                fieldName="z"
                :root="_self"
              />
              <exo-path-select-row
                v-if="!isMultiAxis"
                :field="configuration.y"
                fieldName="y"
                :root="_self"
                :style="isLineChart ? 'margin-bottom: -14px;' : ''"
              />
              <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="12"></div>
              <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="16"></div>
              <div v-if="isMultiAxis">
                <exo-path-select-row
                  v-for="(yn, index) in configuration.multiAxis"
                  :field="yn"
                  :fieldName="'y' + (index + 1)"
                  :key="yn.id"
                  :root="_self"
                  gt_ParamOrbitTransfert2-v-step="21"
                />
                <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="22"></div>
                <exo-multi-axis-options :root="_self"></exo-multi-axis-options>
              </div>
              <v-col cols="3" class="pt-3" v-if="isLineChart">
                <v-btn
                  class="mt-3"
                  v-on:click="switchAxes"
                  :color="$vuetify.theme.dark ? '' : 'grey lighten-4'"
                >
                  Switch axis
                </v-btn>
              </v-col>
              <exo-path-select-row
                fieldName="x"
                :field="configuration.x"
                :root="_self"
              />
              <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="13"></div>
              <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="17"></div>
              <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="23"></div>
            </v-col>
            <v-col class="px-3">
              <v-divider
                class="mb-0"
                v-if="isLineChart || isBarChart"
              ></v-divider>
              <v-row class="ma-2" v-if="isLineChart || isBarChart">
                <exo-parameter-row
                  v-for="(parameter, index) in configuration.parameters"
                  :key="parameter.id"
                  :parameter="parameter"
                  :index="index"
                  :root="_self"
                />
                <v-btn
                  class="ma-2"
                  color="primary"
                  dark
                  v-on:click="addNewParameter"
                >
                  <v-icon dark>add</v-icon>
                  Add Parameter
                </v-btn>
              </v-row>
            </v-col>
            <v-divider class="mb-0"></v-divider>
            <v-col class="p-0 m-0">
              <v-row no-gutters>
                <v-col cols="8">
                  <v-text-field
                    value="Display 0 on axis"
                    dense
                    solo
                    flat
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-checkbox
                    class="p-3 m-0"
                    v-model="configuration.xzero"
                    id="inputxzero"
                    label="X"
                  ></v-checkbox>
                </v-col>
                <v-col cols="2">
                  <v-checkbox
                    class="p-3 m-0"
                    v-model="configuration.yzero"
                    id="inputyzero"
                    label="Y"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col v-if="!isXAxisTypeString || !isYAxisTypeString" cols="8">
                  <v-text-field
                    value="Logarithmic axis"
                    dense
                    solo
                    flat
                  ></v-text-field>
                </v-col>
                <v-col v-if="!isXAxisTypeString" cols="2">
                  <v-checkbox
                    class="p-3 m-0"
                    v-model="configuration.xlog"
                    id="inputxlog"
                    label="X"
                  ></v-checkbox>
                </v-col>
                <v-col v-if="!isYAxisTypeString" cols="2">
                  <v-checkbox
                    class="p-3 m-0"
                    v-model="configuration.ylog"
                    id="inputylog"
                    label="Y"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-col>
            <div v-if="isWarningMessages">
              <v-alert
                show
                variant="warning"
                v-for="item in this.warningMessages"
                v-bind:key="item.id"
              >
                {{ item.message }}
              </v-alert>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
      <div>
        <v-divider class="m-0"></v-divider>
        <v-container>
          <v-btn
            type="submit"
            @click="updatePlotData"
            id="submitBtn"
            variant="primary"
            class="lg"
            >Draw</v-btn
          >
          <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="14"></div>
          <div v-if="drawer" gt_ParamOrbitTransfert2-v-step="24"></div>
          <v-btn
            type="button"
            @click="cancelPlot()"
            id="cancelPlotBtn"
            variant="primary"
            class="lg"
            >Cancel</v-btn
          >
        </v-container>
      </div>
    </v-navigation-drawer>
    <v-row>
      <v-col shrink>
        <div :id="plotingAreaName" class="elevation-5"></div>
      </v-col>
    </v-row>
    <v-tour
      name="paramOrbitalTransfer2TourAnaPlot"
      :steps="steps_analytical_plot_configuration"
      :callbacks="customCallbacksPlot"
      :options="noSkipStepOption"
    ></v-tour>
  </div>
</template>

<script>
// import mixins dependencies
import loadPlotComponents from '../../components/Plot/plotComponents.js'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'
import logHandler from '../../mixins/logHandler'
import dataTools from '../../tools/data.js'
import Plotly from 'plotly.js-dist'
import { noSkipStepOption } from '../../components/GuidedTutorials/guidedTutorialConstraints.js'
import guidedTutorialParamOrbitalTransfer2, {
  steps_analytical_plot_configuration
} from '../../components/GuidedTutorials/guidedTutorialParamOrbitalTransfer2.js'
import { mapGetters } from 'vuex'
import 'vue-grid-layout'

loadPlotComponents()

export default {
  name: 'analytical-plot-config',

  mixins: [plotLayout, plotInput, plotData, logHandler],

  mounted() {
    this.$root.$on('current-results-updated', this.currentResultsUpdatedHandler)
    this.$root.$on('set-plot-config', this.setPlotConfigHandler)
  },

  beforeDestroy() {
    this.hidePlotingArea()
    this.$root.$off(
      'current-results-updated',
      this.currentResultsUpdatedHandler
    )
    this.$root.$off('set-plot-config', this.setPlotConfigHandler)
  },

  computed: {
    ...mapGetters([
      'currentMission',
      'currentResultsLog',
      'gt_OrbitTransferParam2'
    ]),
    isWindowTemporary() {
      const isTemporary =
        !this.gt_OrbitTransferParam2.isActive ||
        this.currentStep === -1 ||
        this.currentStep === 4 ||
        this.currentStep === 7 ||
        this.currentStep === 10 ||
        this.makeTemporary
      this.makeTemporary = false
      return isTemporary
    },
    isWindowPermanent() {
      return !this.isWindowTemporary
    },

    backgroundStarStyle() {
      return (
        'background: #3BE287'
      )
    },

    isWarningMessages() {
      return this.warningMessages.length > 0
    },

    isMap() {
      return (
        this.configuration.type === 'Contour map' ||
        this.configuration.type === 'Heat Map' ||
        this.configuration.type === 'Surface' ||
        this.configuration.type === '3D chart'
      )
    },

    isXAxisTypeString() {
      return this.dataTypeXIsString
    },

    isYAxisTypeString() {
      return this.dataTypeYIsString
    }
  },

  data() {
    // TODO MFI these data should be in plotLayout, plotInput, plotData
    return {
      drawer: false,
      plotTypes: [
        'Line/Scatter chart',
        'Multi axis',
        'Contour map',
        'Heat Map',
        'Bar chart'
      ] /*, 'Surface' */,
      scatterModesLineChart: [
        { label: 'Lines + Markers', value: 'lines+markers' },
        { label: 'Lines', value: 'lines' },
        { label: 'Markers', value: 'markers' },
        { label: 'Markers + Text', value: 'markers+text' }
      ],
      scatterModesMultiAxis: [
        { label: 'Lines + Markers', value: 'lines+markers' },
        { label: 'Lines', value: 'lines' },
        { label: 'Markers', value: 'markers' }
      ],
      targetTypes: ['Initial', 'Final', 'Min', 'Max'],
      scatterShapes: [
        { label: 'Linear', value: 'linear' },
        { label: 'Spline', value: 'spline' }
      ],
      currentStep: -1,
      makeTemporary: false,
      noSkipStepOption: noSkipStepOption,
      steps_analytical_plot_configuration: steps_analytical_plot_configuration,
      customCallbacksPlot: {
        onNextStep: (currentStep) => {
          guidedTutorialParamOrbitalTransfer2.onNextStepPlot(this, currentStep)
        },
        onSkip: () => {
          guidedTutorialParamOrbitalTransfer2.stopAllTours(this)
        },
        onFinish: () => {
          guidedTutorialParamOrbitalTransfer2.onFinishPlot(this)
        }
      },
      plotingAreaName: 'plotingAreaAnalytical',
      plotData: [],
      acceptPathUpdates: true,
      displayForm: true,
      configuration: null,
      plotingArea: null,
      filteredResults: [],
      warningMessages: [],
      type: 'ParametricStudyResult',
      isDrawn: false
    }
  },

  methods: {
    currentResultsUpdatedHandler() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === 'ParametricStudyRequest'
      ) {
        this.updateCurrentMission()
      }
    },
    setPlotConfigHandler(config, plotingArea) {
      this.configuration = config
      this.plotingArea = plotingArea
      this.updateCurrentMission()
      this.applyFilters()
    },

    setPlotData() {
      if (this.currentResultsLog.plotData == null) {
        // use cache to save the harmonized structure which can be long to create (0.5 to 1 seconds for 2000 results)
        this.currentResultsLog.plotData = dataTools.harmonizeStructure(
          this.currentResultsLog.results
        )
      }
      this.plotData = this.currentResultsLog.plotData
    },

    // Cant export there 3 next function because they are called from another component
    updateCurrentMission() {
      this.filteredResults = []
      this.colorIndex = 0
      this.plotData = []
      if (
        this.currentMission != null &&
        this.currentResultsLog != null &&
        this.currentResultsLog.results != null
      ) {
        this.mission = this.currentMission
        this.setPlotData()
        if (this.mission != null) {
          this.hidePlotingArea()
          // let config = this.buildEmptyConfiguration()
          // Vue.set(this.configuration, config)
          // this.configuration.name = this.mission.name
          this.filteredResults = this.plotData
          this.displayForm = true
        } else {
          this.hidePlotingArea()
        }
      }
    },

    hidePlotingArea() {
      if (document.getElementById(this.plotingAreaName)) {
        this.isDrawn = false
        try {
          Plotly.purge(this.plotingAreaName)
        } catch (e) {}
      }
    },

    cancelPlot() {
      this.drawer = false
    },

    pathUpdated(parameter) {
      let fieldType = dataTools.getFieldTypeFromPath(
        parameter.path,
        this.type,
        this.currentResultsLog.scriptConfig
      )
      parameter.unitOptions = dataTools.getAvailableUnits(fieldType)
      parameter.unit =
        dataTools.getDefaultGuiUnit(fieldType) === 'DHMS'
          ? 'days'
          : dataTools.getDefaultGuiUnit(fieldType)
      parameter.label = dataTools.getDataLabel(parameter.path)
    },

    // Parameters
    addNewParameter() {
      this.configuration.parameters.push(this.buidDefaultPlotParameter())
    },

    removeParameter(index) {
      this.configuration.parameters.splice(index, 1)
    },

    // Filters
    addFilter() {
      this.configuration.filters.push({
        value: [],
        min: '',
        max: '',
        treevalue: null,
        unit: '',
        unitOptions: [],
        valueOptions: [],
        path: []
      })
    },

    removeFilter(index) {
      this.configuration.filters.splice(index, 1)
      this.applyFilters()
    },

    applyFilters() {
      this.filteredResults = this.plotData
      for (let i = 0; i < this.configuration.filters.length; i++) {
        let path = this.configuration.filters[i].path
        let value = this.configuration.filters[i].value
        this.filteredResults = this.filteredResults.filter((element) =>
          value.includes(dataTools.getValue(element, path))
        )
      }
    },

    removeInvalidFilters() {
      for (let i = 0; i < this.configuration.filters.length; i++) {
        let path = this.configuration.filters[i].path
        if (path.length == 0) {
          this.removeFilter(i)
        }
      }
    },

    listFilterOptions(filter) {
      let values = []
      if (filter && filter.path && filter.path.length > 0) {
        values = dataTools.getUniqueValues(this.plotData, filter.path)
      }
      return values
    },

    pathFilterUpdated(parameter) {
      if (parameter.treevalue) {
        parameter.path = parameter.treevalue.split('.')
        let fieldType = dataTools.getFieldTypeFromPath(
          parameter.path,
          this.type,
          this.currentResultsLog.scriptConfig
        )
        parameter.unitOptions = dataTools.getAvailableUnits(fieldType)
        parameter.unit =
          dataTools.getDefaultGuiUnit(fieldType) === 'DMHS'
            ? 'days'
            : dataTools.getDefaultGuiUnit(fieldType)
        parameter.valueOptions = dataTools.getUniqueValues(
          this.plotData,
          parameter.path
        )
        parameter.value = []
        this.applyFilters()
      }
    },

    getFilters() {
      const config = this.configuration
      if (config.filters[0] && config.filters[0].path.length !== 0) {
        config.filtersToDisplay = null
        const filtersArray = []
        for (let i = 0; i < config.filters.length; i++) {
          let filter = config.filters[i].path[0]
          if (filter === 'maneuver' || 'spacecraft') {
            for (let j = 1; j < config.filters[i].path.length; j++) {
              filter += ' ' + config.filters[i].path[j]
            }
          }
          const standardizedFilter = filter.split(/(?=[A-Z])/).join(' ')
          filtersArray.push(standardizedFilter.toLowerCase())
        }
        config.filtersToDisplay = filtersArray
      }
    },

    // Draw clicked
    updatePlotData() {
      if (this.gt_OrbitTransferParam2.isActive) {
        guidedTutorialParamOrbitalTransfer2.handleVisibilityPlotWindow(this)
      }
      let config = this.configuration == null ? {} : this.configuration
      this.colorIndex = 0
      config.data = []
      config.warningMessages = []
      this.initLayout(config)
      this.removeInvalidFilters()
      this.applyFilters()
      this.getFilters()
      if (this.isMultiAxis) {
        this.addMultiAxisTrace()
      } else {
        if (this.isMap) {
          this.addMapTrace()
        } else if (this.isBarChart) {
          if (config.parameters.length === 0) {
            this.addBarChart('', this.filteredResults)
          } else {
            this.addRecursiveTrace(0, this.filteredResults, [])
          }
        } else if (this.isLineChart) {
          if (config.parameters.length === 0) {
            this.addTrace('', this.filteredResults)
          } else {
            this.addRecursiveTrace(0, this.filteredResults, [])
          }
        }
        config.layout.xaxis = {
          ...config.layout.xaxis,
          gridcolor: this.$vuetify.theme.dark ? '#656565' : '',
          linecolor: this.$vuetify.theme.dark ? '#656565' : '',
          zerolinecolor: this.$vuetify.theme.dark ? 'white' : '#121212',
          tickfont: {
            color: this.$vuetify.theme.dark ? 'white' : '#121212'
          }
        }
        let textColor = this.$vuetify.theme.dark ? 'white' : 'black'
        config.layout.yaxis = {
          title: {
            text: config.y.label + dataTools.getUnitLabel(config.y.unit),
            font: {
              family: "'Roboto', sans-serif",
              size: 14,
              color: this.isMap ? textColor : '#2a94de'
            }
          },
          showline: true,
          zeroline: true,
          rangemode: config.yzero ? 'tozero' : 'normal',
          hoverformat: '.' + dataTools.getNbToDisplay(config.x.unit) + 'f',
          tickfont: {
            color: this.isMap ? textColor : '#2a94de'
          },
          gridcolor: this.$vuetify.theme.dark ? '#656565' : '',
          linecolor: this.$vuetify.theme.dark ? '#656565' : '',
          zerolinecolor: this.$vuetify.theme.dark ? 'white' : '#121212'
        }
        config.layout.plot_bgcolor = this.$vuetify.theme.dark
          ? '#121212'
          : 'white'
        config.layout.paper_bgcolor = this.$vuetify.theme.dark
          ? '#121212'
          : 'white'
        if (config.ylog) {
          config.layout.yaxis.type = 'log'
          config.layout.yaxis.autorange = true
        }
      }
      this.mission.plotConfiguration = JSON.parse(JSON.stringify(config)) // copy values, not ref
      this.updatePlot(this.plotingArea)
      this.configuration.isDrawn = true
      this.drawer = false
    },

    checkUncompleteConfiguration(values, results) {
      this.warningMessages = []
      // find all duplicated values on x axis and check the y is identical
      // if not , see all fields changing for a given x
      let multipleYindexes = new Set()
      let alreadyAnalyzedIndexes = new Set()
      for (let i = 0; i < values.x.length - 1; i++) {
        if (!alreadyAnalyzedIndexes.has(i)) {
          let currentX = values.x[i]
          for (let j = 0; j < values.x.length; j++) {
            let otherX = values.x[j]
            if (currentX === otherX) {
              if (
                values.y[i] !== values.y[j] &&
                !isNaN(values.y[i]) &&
                !isNaN(values.y[j])
              ) {
                multipleYindexes.add(i)
                alreadyAnalyzedIndexes.add(j)
              }
            }
          }
        }
      }
      if (multipleYindexes.size > 0) {
        let message =
          'Warning : You may have forgotten to fix a parameter or a filter, there will be multiple y values for each x value. Available fields : '
        let changingFields = new Set()
        for (let index of multipleYindexes) {
          let currentX = values.x[index]
          let fieldType = dataTools.getFieldTypeFromPath(
            this.configuration.x.path,
            this.type,
            this.currentResultsLog.scriptConfig
          )
          let currentXsi = dataTools.convert2si(
            fieldType,
            currentX,
            this.configuration.x.unit
          )
          let partialResult = results.filter(
            (element) =>
              dataTools.getValue(element, this.configuration.x.path) ===
              currentXsi
          )
          if (partialResult.length === 0) {
            this.displayMessage(
              'Warning : You may have forgotten to fix a parameter or a filter, there will be multiple y values for each x value.',
              '',
              'notification',
              'warn'
            )
            // TODO To be fixed : this.displayMessage('Internal error : can not check missing fields', '', 'notification', 'error')
            return
          }
          let options = this.listAxisOptionsEvolvingOrConstants(
            partialResult,
            false
          )
          options.forEach((opt) => {
            changingFields.add(opt.text)
          })
        }
        if (changingFields.size > 15) {
          this.displayMessage(
            'Warning : You may have forgotten to fix a parameter or a filter, there will be multiple y values for each x value.',
            '',
            'notification',
            'warn'
          )
          return
        }

        message += Array.from(changingFields).join(' , ')
        this.displayMessage(message, '', 'notification', 'warn')
      }
    },

    addTrace(traceName, results) {
      let config = this.configuration
      let values = this.initScatter(config)
      if (traceName !== '') {
        values.name = traceName
      }
      values.x = this.getPlotValues(config.x, results)
      values.y = this.getPlotValues(config.y, results)
      this.setBasicLayoutY(config)
      this.setScatterValues(config, values)
      this.checkUncompleteConfiguration(values, results)

      config.data.push(values)
    },

    addBarChart(traceName, results) {
      let config = this.configuration
      let values = this.initBarChart(config)
      if (traceName !== '') {
        values.name = traceName
      }
      values.x = this.getPlotValues(config.x, results)
      values.y = this.getPlotValues(config.y, results)
      this.setBasicLayoutY(config)
      this.checkUncompleteConfiguration(values, results)

      config.data.push(values)
    },

    addRecursiveTrace(parameterIndex, filteredResults, traceNames) {
      let config = this.configuration
      let parameter = config.parameters[parameterIndex]
      let fieldType = dataTools.getFieldTypeFromPath(
        parameter.path,
        this.type,
        this.currentResultsLog.scriptConfig
      )
      let paramLabel = dataTools.getUnitLabel(parameter.unit)
      let uniqueVals = dataTools.getUniqueValues(
        filteredResults,
        parameter.path
      )
      for (let paramValue of uniqueVals) {
        let newTraceNames = JSON.parse(JSON.stringify(traceNames)) // copy values, not ref to not modify source
        if (typeof paramValue === 'string') {
          newTraceNames.push(
            dataTools.convertFromSi(fieldType, paramValue, parameter.unit) +
              paramLabel
          )
        } else {
          const powerOfTen = dataTools.getNbToDisplay(
            dataTools.getSiUnit(fieldType)
          )
          newTraceNames.push(
            dataTools.convertFromSi(
              fieldType,
              Math.round((+paramValue + Number.EPSILON) * 10 ** powerOfTen) /
                10 ** powerOfTen,
              parameter.unit
            ) + paramLabel
          )
        }
        let currentResult = filteredResults.filter(
          (element) =>
            dataTools.getValue(element, parameter.path) === paramValue
        )
        if (parameterIndex < config.parameters.length - 1) {
          this.addRecursiveTrace(
            parameterIndex + 1,
            currentResult,
            newTraceNames
          )
        } else {
          // last parameter : add the trace
          if (this.isLineChart) {
            this.addTrace(newTraceNames.join(' / '), currentResult)
          } else if (this.isBarChart) {
            config.layout = { ...config.layout, barmode: 'group' }
            this.addBarChart(newTraceNames.join(' / '), currentResult)
          }
        }
      }
    },

    addMultiAxisTrace() {
      let config = this.configuration
      let values = { x: this.getPlotValues(config.x, this.filteredResults) }

      for (let index in config.multiAxis) {
        let yn = config.multiAxis[index]
        let trace = this.initScatter(config)
        trace.x = values.x
        trace.y = this.getPlotValues(yn, this.filteredResults)
        config.data.push(trace)
        this.setMultiAxisLayout(config, index, trace, config.multiAxis)
      }
    },

    addMapTrace() {
      let config = this.configuration
      let currentResult = this.filteredResults
      let traceType = this.getMapType(this.configuration.type)
      let trace = this.setMapLayout(traceType, config)
      trace.x = this.getPlotValues(config.x, currentResult).map((elem) =>
        isNaN(elem)
          ? NaN
          : dataTools.displayTypeDigitsFromUnit(config.x.unit, elem)
      )

      trace.y = this.getPlotValues(config.y, currentResult).map((elem) =>
        isNaN(elem)
          ? NaN
          : dataTools.displayTypeDigitsFromUnit(config.y.unit, elem)
      )
      trace.z = this.getPlotValues(config.z, currentResult)
      if (traceType === 'surface') {
        config.layout.width = 1200
        config.layout.height = 1000
        this.setSurfaceData(trace)
      }
      this.setMapAxisTitles(config)
      config.data.push(trace)
    }
  }
}
</script>

<style scoped>
.d-grid-nav {
  height: 100%;
  display: inline-grid;
}

.card-bottom {
  display: inline-block;
  align-self: flex-end;
}
</style>
